import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import BannerSection from "../section/BannerSectionWFull";
import dayjs from "dayjs";
import "dayjs/locale/th";
import { EyeIcon } from "@heroicons/react/24/outline";

const clientsThis = async (headers) => {
  try {
    const res = await Axios.get(
      `${process.env.REACT_APP_API_URL}/api/clients/this`,
      { headers }
    );
    return res.data?.data?.variables;
  } catch {
    return "";
  }
};

const getCapaign = async (id) => {
  try {
    const res = await Axios.get(
      `${process.env.REACT_APP_CORPORATE_API_URL}/api/campaign?id=${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_CORPORATE_X_API_KEY,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.log(`bigc-campaign: ${error}`);
  }
};

const set_view = async (payload) => {
  try {
    const res = await Axios.post(
      `${process.env.REACT_APP_CORPORATE_API_URL}/api/set_view`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_CORPORATE_X_API_KEY,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.log(`bigc-campaign: ${error}`);
  }
};

// ภาษาไทย: 28 พฤษภาคม 2568
const formatDateTH = (dateStr) => {
  const date = dayjs(dateStr).locale("th");
  const buddhistYear = date.year() + 543;
  return date.format(`D MMMM ${buddhistYear}`);
};

// ภาษาอังกฤษ: 28 May 2025
const formatDateEN = (dateStr) => {
  return dayjs(dateStr).locale("en").format("D MMM YYYY");
};

function CampaignDetail() {
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id");
  const { friendlyURL } = useParams();
  const { t, i18n } = useTranslation();
  const [sections, setSections] = useState([]);
  const [responseStatus, setResponseStatus] = useState("");
  const [campaign, setCampaign] = useState(null);
  const language = i18n.language.toUpperCase();

  const [campaignDate, setCampaignDate] = useState("");

  const headers = useMemo(
    () => ({
      //Authorization: `Bearer ${authToken}`,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      //"x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [i18n.language]
  );

  useEffect(() => {
    sessionStorage.setItem("previousPath", window.location.pathname);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      /*if (!authToken || !userId) {
        // console.log("Authentication token or user ID is missing.");
        return;
      }*/
      try {
        const pageId = await clientsThis(headers);

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/pages/${pageId.root_page_id}`;
        const response = await Axios.get(apiUrl, {
          headers: headers,
        });
        const result = response.data?.data?.sections || [];
        await setSections(result);
      } catch (err) {
        console.error("Error fetching data:", err.message);
        //setError(err.message);
      }
    };

    fetchData();
  }, [i18n]);

  useEffect(() => {
    if (!id) return;
    setCampaign(null);
    setResponseStatus("");
    const fetchCampaign = async () => {
      const getCampaign = await getCapaign(id);
      if (getCampaign && getCampaign.data) {
        setCampaign(getCampaign.data);

        setTimeout(() => {
          set_view({ id: id, type: "campaign" });
        }, 3000);

        if (i18n.language.toUpperCase() === "TH") {
          setCampaignDate(formatDateTH(getCampaign.data.publish_date));
        } else {
          setCampaignDate(formatDateEN(getCampaign.data.publish_date));
        }

        setResponseStatus(getCampaign.status);
      } else {
        setCampaign(null);
        setResponseStatus("warning");
      }
    };
    fetchCampaign();
  }, [id]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const hotspots = document.querySelectorAll("a.campaign-hotspot");
      hotspots.forEach((el) => {
        // ตรวจว่าติด handler แล้วหรือยัง (ป้องกันซ้ำ)
        if (!el.dataset.tracked) {
          el.dataset.tracked = "true";

          el.addEventListener("click", () => {
            const href = el.getAttribute("href");
            const title = el.getAttribute("title");

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "campaign_click_hotspot",
              campaign_ref: id,
              campaign_id: friendlyURL,
              campaign_name:
                i18n.language.toUpperCase() === "TH"
                  ? campaign?.name_th
                  : campaign?.name_en,
              link_url: href,
              hotspot_label: title || "unknown",
              language: i18n.language.toUpperCase(),
            });
          });
        }
      });
    });

    // สังเกต container ที่แสดง campaign detail
    const container = document.querySelector(".campaign-detail-page");
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [campaign, friendlyURL, id, i18n.language]);

  return (
    <div>
      <BannerSection sections={sections} />

      <div
        style={{
          width: "100%",
          maxWidth: 800,
          margin: "auto",
        }}
      >
        <div className="flex flex-col items-center bg-secondary-background-gray bg-gray-100 pt-3">
          {!campaign && responseStatus === "warning" ? (
            <p className="text-center text-red-500">
              {t("campaign_not_found")}
            </p>
          ) : campaign ? (
            <>
              {campaign.detail_th || campaign.detail_en ? (
                <div
                  className={`flex flex-col items-center mt-10 campaign-detail-page`}
                >
                  <span
                    style={{
                      lineHeight: "1.3em",
                      gap: 8,
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        language === "TH"
                          ? campaign.detail_th.replaceAll(
                              'src="/upload_file/',
                              'src="https://corporate.bigc.co.th/upload_file/'
                            )
                          : campaign.detail_en.replaceAll(
                              'src="/upload_file/',
                              'src="https://corporate.bigc.co.th/upload_file/'
                            ),
                    }}
                  />
                </div>
              ) : (
                <img
                  src={campaign.image}
                  alt={language === "TH" ? campaign.name_th : campaign.name_en}
                  loading="lazy"
                  className="w-full max-w-[700px] object-contain"
                />
              )}
              <div className="w-[90%] h-[2px] bg-green-500 my-10"></div>
              <div className="flex justify-end w-full mb-10 mr-10 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <span>{campaignDate} | </span>
                  <span className="flex items-center gap-1">
                    <EyeIcon className="w-4 h-4" />
                    <span>{campaign.total_view}</span>
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-center text-gray-400">{t("loading")}...</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CampaignDetail;
